import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api.js';
import { Loading, Empty, Avatar, useToast, Modal, Field, PageHeader, Segmented } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import DocumentReview from '../../components/DocumentReview.jsx';
import { currency, csvList, relativeTime } from '../../lib/format.js';

const TABS = [
  ['pending', 'Pending'],
  ['verified', 'Verified'],
  ['rejected', 'Rejected'],
];

const REQUIRED = ['id_proof', 'nursing_licence', 'qualification'];

/** Mirrors the server rule so the button state matches what will be allowed. */
const readyToVerify = (nurse) => {
  const approved = new Set(nurse.documents.filter((d) => d.status === 'approved').map((d) => d.kind));
  return REQUIRED.every((k) => approved.has(k));
};

export default function Verifications() {
  const toast = useToast();
  const [tab, setTab] = useState('pending');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(0);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');
  const [kinds, setKinds] = useState([]);

  useEffect(() => {
    api.get('/api/document-kinds').then((d) => setKinds(d.kinds)).catch(() => setKinds([]));
  }, []);

  /** Fold a per-document decision back into the queue without a full refetch. */
  const applyDocChange = (nurseId, updated) =>
    setRows((list) =>
      list?.map((n) => {
        if (n.id !== nurseId) return n;
        const documents = n.documents.map((d) => (d.id === updated.id ? updated : d));
        return { ...n, documents, unreviewed: documents.filter((d) => d.status === 'pending').map((d) => d.kind) };
      })
    );

  const load = useCallback(() => {
    setRows(null);
    api.get('/api/admin/verifications', { status: tab }).then(setRows).catch(() => setRows([]));
  }, [tab]);

  useEffect(load, [load]);

  const decide = async (id, action, why) => {
    setBusy(id);
    try {
      await api.post(`/api/admin/verifications/${id}`, { action, reason: why });
      setRows((list) => list.filter((r) => r.id !== id));
      setRejecting(null);
      setReason('');
      toast.success(action === 'verify' ? 'Nurse verified and published.' : 'Application rejected.');
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(0);
    }
  };

  return (
    <>
      <PageHeader
        title="Nurse verification"
        subtitle="Check licence details and qualifications before a nurse becomes bookable."
      />

      <Segmented options={TABS} value={tab} onChange={setTab} className="mb-3" style={{ maxWidth: 400 }} />

      {!rows ? (
        <Loading rows={2} />
      ) : rows.length === 0 ? (
        <Empty art={tab === 'pending' ? 'success' : 'documents'} title={`No ${tab} applications`}>
          {tab === 'pending' ? 'Every application has been reviewed.' : 'Nothing in this list.'}
        </Empty>
      ) : (
        <div className="stack">
          {rows.map((n) => (
            <article key={n.id} className="card">
              <div className="card-header">
                <Avatar name={n.name} avatar={n.avatar} />
                <div className="grow">
                  <h3>{n.name}</h3>
                  <div className="small muted">{n.headline || '—'}</div>
                  <div className="tiny dim">
                    {n.email} · {n.phone || 'no phone'} · registered {relativeTime(n.created_at)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="rate">{currency(n.hourly_rate)}</div>
                  <div className="rate-unit">per hour</div>
                </div>
              </div>

              <div className="card-body grid grid-2">
                <Detail label="Licence number" value={n.license_number} mono />
                <Detail label="Experience" value={`${n.experience_years} years`} />
                <Detail label="Gender / city" value={`${n.gender || '—'} · ${n.city || '—'}`} />
                <Detail label="Qualifications" value={n.qualifications} full />
                <Detail label="Certifications" value={csvList(n.certifications).join(' · ') || '—'} full />
                <Detail label="Languages" value={csvList(n.languages).join(', ')} />
                <Detail label="Service areas" value={csvList(n.service_areas).join(', ')} />
                {n.bio && <Detail label="About" value={n.bio} full />}
                {n.rejection_reason && <Detail label="Previous rejection" value={n.rejection_reason} full />}

                <div style={{ gridColumn: '1 / -1' }}>
                  <span className="label">Uploaded documents</span>
                  <DocumentReview nurse={n} kinds={kinds} onChange={applyDocChange} />
                </div>
              </div>

              {tab === 'pending' && (
                <div className="card-footer">
                  <button
                    className="btn btn-success"
                    disabled={busy === n.id || !readyToVerify(n)}
                    onClick={() => decide(n.id, 'verify')}
                    title={readyToVerify(n) ? undefined : 'Approve every required document first'}
                  >
                    {busy === n.id ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <Icon name="shield-check" /> Verify &amp; publish
                      </>
                    )}
                  </button>
                  <button className="btn btn-danger" onClick={() => setRejecting(n)}>
                    <Icon name="close" /> Reject application
                  </button>
                  {!readyToVerify(n) && (
                    <span className="tiny row-tight" style={{ color: 'var(--warning)' }}>
                      <Icon name="warning" size={13} />
                      {n.missingRequired.length
                        ? 'Required documents are missing.'
                        : 'Approve each required document to enable publishing.'}
                    </span>
                  )}
                </div>
              )}
              {tab === 'rejected' && (
                <div className="card-footer">
                  <button className="btn btn-success btn-sm" disabled={busy === n.id} onClick={() => decide(n.id, 'verify')}>
                    Approve after all
                  </button>
                </div>
              )}
              {tab === 'verified' && (
                <div className="card-footer">
                  <button className="btn btn-danger btn-sm" onClick={() => setRejecting(n)}>
                    Revoke verification
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      {rejecting && (
        <Modal
          title={`Reject ${rejecting.name}?`}
          onClose={() => setRejecting(null)}
          size={480}
          footer={
            <>
              <button className="btn btn-ghost" onClick={() => setRejecting(null)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={() => decide(rejecting.id, 'reject', reason)}>
                Reject application
              </button>
            </>
          }
        >
          <p className="muted small">The nurse is notified and can update their profile to re-submit for review.</p>
          <Field label="Reason" id="reject-reason" hint="Shown to the nurse.">
            <textarea
              id="reject-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="The licence number could not be matched against the nursing council register."
            />
          </Field>
        </Modal>
      )}
    </>
  );
}

function Detail({ label, value, full, mono }) {
  return (
    <div style={full ? { gridColumn: '1 / -1' } : undefined}>
      <span className="label">{label}</span>
      <div className="small muted" style={{ marginTop: 3, fontFamily: mono ? 'var(--font-mono)' : undefined }}>
        {value || '—'}
      </div>
    </div>
  );
}
